const EVENT_PREFIX = "HCF Admin Web App (V2):";

export const IMPERSONATION_USER_EVENTS = {
  SESSION_STARTED: `${EVENT_PREFIX} Impersonation session started`,
  SESSION_OPENED: `${EVENT_PREFIX} Impersonation session opened`,
  SESSION_RESTORED: `${EVENT_PREFIX} Impersonation session restored`,
  SESSION_CLOSED: `${EVENT_PREFIX} Impersonation session closed`,
  SESSION_MINIMIZED: `${EVENT_PREFIX} Impersonation session minimized`,
  SESSION_EXPIRED: `${EVENT_PREFIX} Impersonation session expired`,
  SESSION_REFRESHED: `${EVENT_PREFIX} Impersonation session refreshed`,
  SESSION_MAXIMIZE_TOGGLED: `${EVENT_PREFIX} Impersonation session maximize toggled`,
} as const;

export const IMPERSONATION_APP_EVENTS = {
  SESSION_LIMIT_REACHED: `${EVENT_PREFIX} Impersonation session limit reached`,
  TOKEN_SEND_FAILURE: `${EVENT_PREFIX} Impersonation token send failure`,
  AUTH_FAILURE: `${EVENT_PREFIX} Impersonation auth failure`,
  MISSING_COGNITO_GROUP: `${EVENT_PREFIX} Impersonation missing Cognito group`,
} as const;

export const COGNITO_IMPERSONATION_GROUP = "impersonation";

export const MAX_SESSIONS = 4;
export const MAX_WORKPLACE_SESSIONS = 2;
export const MAX_WORKER_SESSIONS = 3;
export const WARNING_THRESHOLD_MS = 3 * 60 * 1000;
export const EXPIRY_MS = 15 * 60 * 1000;
export const EXPIRY_CHECK_INTERVAL_MS = 10_000;
export const DEFAULT_WORKER_WINDOW_SIZE = { width: 375, height: 667 };
export const MIN_WINDOW_SIZE = { width: 300, height: 500 };
export const STALE_CLEANUP_MS = 24 * 60 * 60 * 1000;
export const PHONE_BEZEL_WIDTH = 3;
export const PHONE_BEZEL_RADIUS = 40;
export const HEADER_HEIGHT = 36;

export type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const EDGE_THICKNESS = 8;
const CORNER_SIZE = 14;
// Handles must sit ABOVE the cross-origin iframe, otherwise the iframe consumes
// the initial pointerdown and the user cannot start a resize.
const HANDLE_Z_INDEX = 2;

/**
 * Resize handle styles for all 8 directions. Edge handles are thin strips along
 * each side; corner handles sit in the corners and resize both axes.
 * All handles have zIndex above the iframe so the initial pointerdown lands on
 * the handle even though the iframe is cross-origin and visually below.
 */
export const RESIZE_HANDLE_STYLES: Record<ResizeDirection, Record<string, unknown>> = {
  n: {
    position: "absolute",
    top: 0,
    left: CORNER_SIZE,
    right: CORNER_SIZE,
    height: EDGE_THICKNESS,
    cursor: "ns-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  s: {
    position: "absolute",
    bottom: 0,
    left: CORNER_SIZE,
    right: CORNER_SIZE,
    height: EDGE_THICKNESS,
    cursor: "ns-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  e: {
    position: "absolute",
    right: 0,
    top: CORNER_SIZE,
    bottom: CORNER_SIZE,
    width: EDGE_THICKNESS,
    cursor: "ew-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  w: {
    position: "absolute",
    left: 0,
    top: CORNER_SIZE,
    bottom: CORNER_SIZE,
    width: EDGE_THICKNESS,
    cursor: "ew-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  ne: {
    position: "absolute",
    top: 0,
    right: 0,
    width: CORNER_SIZE,
    height: CORNER_SIZE,
    cursor: "nesw-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  nw: {
    position: "absolute",
    top: 0,
    left: 0,
    width: CORNER_SIZE,
    height: CORNER_SIZE,
    cursor: "nwse-resize",
    zIndex: HANDLE_Z_INDEX,
  },
  se: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: CORNER_SIZE,
    height: CORNER_SIZE,
    cursor: "nwse-resize",
    zIndex: HANDLE_Z_INDEX,
    "&::after": {
      content: '""',
      position: "absolute",
      right: 3,
      bottom: 3,
      width: 6,
      height: 6,
      borderRight: "2px solid",
      borderBottom: "2px solid",
      borderColor: "grey.400",
    },
  },
  sw: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: CORNER_SIZE,
    height: CORNER_SIZE,
    cursor: "nesw-resize",
    zIndex: HANDLE_Z_INDEX,
  },
};

/**
 * When the workplace window is sized to the full viewport, dragging the title
 * bar can't reposition it without violating the viewport-clamp rule. So when
 * the user starts dragging a viewport-sized window, snap it to a smaller size
 * (this fraction of viewport on each axis) so they can actually move it.
 */
export const DRAG_AUTO_SHRINK_RATIO = 0.85;

/**
 * Legacy single-corner resize handle, kept for the worker phone-bezel UI which
 * still resizes only from the bottom-right.
 */
export const RESIZE_HANDLE_SX = RESIZE_HANDLE_STYLES.se;

export function getHeaderBackgroundColor(
  isClosing: boolean,
  error: string | undefined,
  isWarning: boolean
): string {
  if (isClosing) {
    return "grey.600";
  }

  if (error) {
    return "error.main";
  }

  if (isWarning) {
    return "warning.main";
  }

  return "primary.main";
}
